import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const DEFAULT_IMAGE = 'https://images.unsplash.com/photo-1589688934051-ca9059383f4f?w=400&q=80';

const defaultDescriptions = {
  'Rolex': 'The crown jewel of Swiss watchmaking. Founded in 1905, Rolex is synonymous with precision, prestige, and timeless design.',
  'Patek Philippe': 'The pinnacle of haute horlogerie since 1839. "You never actually own a Patek Philippe. You merely look after it for the next generation."',
  'Audemars Piguet': 'Home of the Royal Oak. Founded in 1875 in Le Brassus, AP is one of the "Holy Trinity" of Swiss watchmaking.',
  'Omega': 'First watch on the Moon. From the Speedmaster to the Seamaster, Omega has been making history since 1848.',
  'Cartier': 'The jeweller of kings and the king of jewellers. Cartier created the first modern wristwatch in 1904 — the Santos.',
  'IWC': 'International Watch Company. Known for pilot\'s watches and the Portugieser, IWC has been crafting precision timepieces in Schaffhausen since 1868.',
  'Jaeger-LeCoultre': 'The watchmaker\'s watchmaker. Founded in 1833, JLC has created over 1,400 calibres and holds hundreds of patents.',
  'Hublot': 'The Art of Fusion. Founded in 1980, Hublot revolutionized watchmaking by combining gold with rubber — and never stopped innovating.',
  'TAG Heuer': 'Swiss avant-garde since 1860. From Formula 1 timing to the Carrera and Monaco, TAG Heuer lives at the intersection of sport and luxury.',
  'Richard Mille': 'The world\'s most technically advanced luxury watches. Each RM is a feat of engineering, using Formula 1 and aerospace materials.',
};

export default function Brands() {
  const navigate = useNavigate();
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchBrands(); }, []);

  const fetchBrands = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/brands`);
      setBrands(res.data);
    } catch (e) {
      console.error('Error fetching brands:', e);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary"></div></div>;
  }

  return (
    <div className="min-h-screen py-8" data-testid="brands-page">
      <div className="mx-auto px-4 sm:px-6 lg:px-8 max-w-[1280px]">
        <div className="mb-10">
          <h1 className="font-display text-3xl sm:text-4xl text-foreground font-semibold mb-2">Our Brands</h1>
          <p className="text-muted-foreground">Explore the world's most prestigious watchmakers</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {brands.map(brand => (
            <Card
              key={brand.id}
              className="group overflow-hidden border-border bg-card cursor-pointer hover:border-primary/30 transition-all"
              onClick={() => navigate(`/catalog?brand=${encodeURIComponent(brand.name)}`)}
              data-testid={`brand-card-${brand.name.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <div className="aspect-[16/9] overflow-hidden bg-black/40">
                <img
                  src={brand.image || DEFAULT_IMAGE}
                  alt={brand.name}
                  className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-300"
                />
              </div>
              <div className="p-5">
                <div className="flex items-center justify-between mb-2">
                  <h2 className="text-lg font-semibold text-foreground">{brand.name}</h2>
                  <span className="text-xs text-muted-foreground">{brand.watchCount} {brand.watchCount === 1 ? 'watch' : 'watches'}</span>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2">
                  {brand.description || defaultDescriptions[brand.name] || 'Discover authenticated timepieces from this prestigious manufacturer.'}
                </p>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}
